import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import axios from "axios";
import { ArrowRight, ArrowLeft, Compass, Sparkles, Mountain, Sun, BookOpen, Flame, FolderOpen, MessageCircle } from "lucide-react";
import PathFinderAvatar from "@/components/PathFinderAvatar";

const STEPS = [
  "welcome",
  "name",
  "intent",
  "season",
  "howto",
  "pathfinder"
];

const INTENTS = [
  { value: "misaligned", label: "I feel misaligned" },
  { value: "overthink", label: "I overthink everything" },
  { value: "clarity", label: "I want clarity" },
  { value: "patterns", label: "I feel stuck in patterns" },
  { value: "understand", label: "I want to understand myself better" },
  { value: "rebuild", label: "I want to rebuild my life from a truer place" },
  { value: "no-coping", label: "When stress spikes, I don't have a coping strategy that actually works" }
];

const SEASONS = [
  { value: "waking", label: "Waking up", icon: Sun, description: "Beginning to see what's been hidden" },
  { value: "letting", label: "Letting go", icon: Mountain, description: "Releasing what no longer serves" },
  { value: "rebuilding", label: "Rebuilding", icon: Sparkles, description: "Creating something new" },
  { value: "overwhelmed", label: "Overwhelmed", icon: Flame, description: "Too much at once, not sure where to start" }
];

const HOW_TO_STEPS = [
  {
    icon: Compass,
    title: "1. Use Daily Compass",
    description: "Check in daily with your signal and reflect on your patterns"
  },
  {
    icon: BookOpen,
    title: "2. Journal Your Journey",
    description: "Capture insights, track patterns, and process your thoughts"
  },
  {
    icon: Flame,
    title: "3. Return Daily",
    description: "Consistency builds awareness. Your streak tracks your commitment"
  },
  {
    icon: FolderOpen,
    title: "4. Explore When Ready",
    description: "Access the Vault and Quests when you want to go deeper"
  }
];

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { user, token, updateUser } = useAuth();
  const [step, setStep] = useState(0);
  const [firstName, setFirstName] = useState("");
  const [intent, setIntent] = useState("");
  const [season, setSeason] = useState("");
  const [loading, setLoading] = useState(false);

  // Redirect if already onboarded
  if (user?.onboarding_complete) {
    navigate("/compass", { replace: true });
    return null;
  }

  const currentStep = STEPS[step];

  const handleNext = () => {
    if (currentStep === "name" && !firstName.trim()) {
      toast.error("Please enter your first name");
      return;
    }
    if (currentStep === "intent" && !intent) {
      toast.error("Please select what brought you here");
      return;
    }
    if (currentStep === "season" && !season) {
      toast.error("Please select your current season");
      return;
    }
    
    if (step < STEPS.length - 1) {
      setStep(step + 1);
    } else {
      handleComplete();
    }
  };

  const handleBack = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/onboarding`, {
        first_name: firstName,
        intent: intent,
        season: season
      }, {
        withCredentials: true
      });
      
      updateUser({ ...user, onboarding_complete: true, name: firstName });
      toast.success("Welcome to Compass!");
      navigate("/compass");
    } catch (error) {
      console.error("Onboarding error:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handlePathFinderChat = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/onboarding`, {
        first_name: firstName,
        intent: intent,
        season: season
      }, {
        withCredentials: true
      });
      
      updateUser({ ...user, onboarding_complete: true, name: firstName });
      navigate("/pathfinder");
    } catch (error) {
      console.error("Onboarding error:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="w-full max-w-2xl">
        {/* Progress indicator */}
        <div className="flex items-center justify-center gap-2 mb-12">
          {STEPS.map((stepName, index) => (
            <div
              key={stepName}
              className={`h-1.5 w-12 rounded-full transition-colors duration-300 ${
                index <= step ? "bg-primary" : "bg-muted"
              }`}
            />
          ))}
        </div>

        <div className="glass-card p-8 md:p-12 animate-fade-in" data-testid="onboarding-card">
          {/* Welcome Step */}
          {currentStep === "welcome" && (
            <div className="text-center">
              <Compass className="w-16 h-16 text-primary mx-auto mb-8" strokeWidth={1} />
              <h1 className="text-3xl md:text-4xl text-foreground mb-6">
                Compass is how you stay aligned when pressure tries to pull you off course.
              </h1>
              <p className="text-muted-foreground text-lg mb-12 max-w-md mx-auto">
                Self-awareness without daily correction changes nothing. You need a system that brings you back, again and again.
              </p>
              <Button 
                className="btn-primary py-6 px-12"
                onClick={handleNext}
                data-testid="onboarding-welcome-next"
              >
                Begin
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          )}

          {/* Name Step */}
          {currentStep === "name" && (
            <div className="space-y-8">
              <div className="text-center">
                <h2 className="text-2xl md:text-3xl text-foreground mb-2">What shall we call you?</h2>
                <p className="text-muted-foreground">Your name helps us personalise your journey.</p>
              </div>
              
              <div className="max-w-sm mx-auto">
                <Label htmlFor="firstName" className="text-foreground">First Name</Label>
                <Input
                  id="firstName"
                  type="text"
                  placeholder="Enter your first name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="input-field mt-2 text-center text-lg h-14"
                  autoFocus
                  data-testid="onboarding-name-input"
                />
              </div>

              <div className="flex justify-center gap-4">
                <Button 
                  variant="ghost" 
                  className="btn-ghost"
                  onClick={handleBack}
                >
                  <ArrowLeft className="mr-2 w-5 h-5" />
                  Back
                </Button>
                <Button 
                  className="btn-primary py-6 px-12"
                  onClick={handleNext}
                  data-testid="onboarding-name-next"
                >
                  Continue
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            </div>
          )}

          {/* Intent Step */}
          {currentStep === "intent" && (
            <div className="space-y-8">
              <div className="text-center">
                <h2 className="text-2xl md:text-3xl text-foreground mb-2">What brought you here, {firstName}?</h2>
                <p className="text-muted-foreground">Select the option that resonates most.</p>
              </div>
              
              <RadioGroup
                value={intent}
                onValueChange={setIntent}
                className="grid gap-3"
                data-testid="onboarding-intent-group"
              >
                {INTENTS.map((item) => (
                  <Label
                    key={item.value}
                    htmlFor={item.value}
                    className={`flex items-center p-4 rounded-xl border cursor-pointer transition-colors duration-300 ${
                      intent === item.value 
                        ? "border-primary bg-primary/10" 
                        : "border-white/10 hover:border-white/20 bg-muted/30"
                    }`}
                  >
                    <RadioGroupItem value={item.value} id={item.value} className="mr-4" />
                    <span className="text-foreground font-bold">{item.label}</span>
                  </Label>
                ))}
              </RadioGroup>

              <div className="flex justify-center gap-4">
                <Button 
                  variant="ghost" 
                  className="btn-ghost"
                  onClick={handleBack}
                >
                  <ArrowLeft className="mr-2 w-5 h-5" />
                  Back
                </Button>
                <Button 
                  className="btn-primary py-6 px-12"
                  onClick={handleNext}
                  data-testid="onboarding-intent-next"
                >
                  Continue
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            </div>
          )}

          {/* Season Step */}
          {currentStep === "season" && (
            <div className="space-y-8">
              <div className="text-center">
                <h2 className="text-2xl md:text-3xl text-foreground mb-2">Where are you in your journey?</h2>
                <p className="text-muted-foreground">Choose your current season.</p>
              </div>
              
              <RadioGroup
                value={season}
                onValueChange={setSeason}
                className="grid gap-4"
                data-testid="onboarding-season-group"
              >
                {SEASONS.map((item) => (
                  <Label
                    key={item.value}
                    htmlFor={item.value}
                    className={`flex items-start p-5 rounded-xl border cursor-pointer transition-colors duration-300 ${
                      season === item.value 
                        ? "border-primary bg-primary/10" 
                        : "border-white/10 hover:border-white/20 bg-muted/30"
                    }`}
                  >
                    <RadioGroupItem value={item.value} id={item.value} className="mr-4 mt-1" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <item.icon className="w-5 h-5 text-primary" strokeWidth={1.5} />
                        <span className="text-foreground font-bold">{item.label}</span>
                      </div>
                      <p className="text-muted-foreground text-sm font-medium">{item.description}</p>
                    </div>
                  </Label>
                ))}
              </RadioGroup>

              <div className="flex justify-center gap-4">
                <Button 
                  variant="ghost" 
                  className="btn-ghost"
                  onClick={handleBack}
                >
                  <ArrowLeft className="mr-2 w-5 h-5" />
                  Back
                </Button>
                <Button 
                  className="btn-primary py-6 px-12"
                  onClick={handleNext}
                  data-testid="onboarding-season-next"
                >
                  Continue
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            </div>
          )}

          {/* How To Use Step */}
          {currentStep === "howto" && (
            <div className="space-y-8">
              <div className="text-center">
                <h2 className="text-2xl md:text-3xl text-foreground mb-2">How to use Compass</h2>
                <p className="text-muted-foreground">Your daily practice for transformation.</p>
              </div>
              
              <div className="space-y-4">
                {HOW_TO_STEPS.map((item) => (
                  <div
                    key={item.title}
                    className="flex items-start gap-4 p-4 rounded-xl bg-muted/30 border border-white/10"
                    data-testid={`howto-step-${item.title.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center shrink-0">
                      <item.icon className="w-6 h-6 text-primary" strokeWidth={1.5} />
                    </div>
                    <div>
                      <h3 className="text-foreground font-medium mb-1">{item.title}</h3>
                      <p className="text-muted-foreground text-sm">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="p-4 rounded-xl bg-primary/10 border border-primary/20">
                <p className="text-foreground/80 text-sm text-center">
                  <span className="font-medium">Start simple:</span> Open your Daily Compass each day. 
                  That's all you need to begin.
                </p>
              </div>

              <div className="flex justify-center gap-4">
                <Button 
                  variant="ghost" 
                  className="btn-ghost"
                  onClick={handleBack}
                >
                  <ArrowLeft className="mr-2 w-5 h-5" />
                  Back
                </Button>
                <Button 
                  className="btn-primary py-6 px-12"
                  onClick={handleNext}
                  disabled={loading}
                  data-testid="onboarding-howto-next"
                >
                  Continue
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            </div>
          )}

          {/* PathFinder Introduction Step */}
          {currentStep === "pathfinder" && (
            <div 
              className="rounded-2xl p-6 md:p-8"
              style={{ 
                backgroundColor: '#0f1e1a',
                border: '1.5px solid #BF9B5A'
              }}
            >
              {/* PathFinder Avatar */}
              <div className="flex justify-center mb-6">
                <PathFinderAvatar size={72} />
              </div>
              
              {/* Title */}
              <h2 
                className="text-2xl md:text-3xl font-heading text-center mb-6"
                style={{ color: '#BF9B5A' }}
              >
                Meet PathFinder
              </h2>
              
              {/* Message */}
              <div 
                className="rounded-xl p-4 mb-6"
                style={{ backgroundColor: 'rgba(191, 155, 90, 0.1)' }}
              >
                <p 
                  className="text-sm md:text-base leading-relaxed"
                  style={{ color: 'rgba(255, 255, 255, 0.9)' }}
                >
                  Hi, I'm PathFinder — your AI companion inside Compass.
                </p>
                <p 
                  className="text-sm md:text-base leading-relaxed mt-4"
                  style={{ color: 'rgba(255, 255, 255, 0.9)' }}
                >
                  I read your journal entries, track your patterns, and help you understand yourself more deeply over time.
                </p>
                <p 
                  className="text-sm md:text-base leading-relaxed mt-4"
                  style={{ color: 'rgba(255, 255, 255, 0.9)' }}
                >
                  I notice when your energy shifts, when stress is building before you do, and when you're closer to a breakthrough than you realise.
                </p>
                <p 
                  className="text-sm md:text-base leading-relaxed mt-4"
                  style={{ color: 'rgba(255, 255, 255, 0.9)' }}
                >
                  I ask questions instead of giving answers — because your insight is always more powerful than my advice.
                </p>
                <p 
                  className="text-sm md:text-base leading-relaxed mt-4"
                  style={{ color: '#BF9B5A' }}
                >
                  Free members get a taste of what I can do. When you're ready to go deeper, I'll be here.
                </p>
              </div>
              
              {/* Buttons */}
              <div className="space-y-3">
                <Button
                  onClick={handlePathFinderChat}
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 py-4 rounded-xl font-medium transition-all hover:scale-[1.02]"
                  style={{ 
                    backgroundColor: '#BF9B5A', 
                    color: '#0b2821',
                    border: 'none'
                  }}
                  data-testid="onboarding-chat-pathfinder"
                >
                  <MessageCircle className="w-5 h-5" />
                  {loading ? "Setting up..." : "Chat with PathFinder"}
                </Button>
                
                <Button
                  onClick={handleComplete}
                  disabled={loading}
                  variant="ghost"
                  className="w-full py-4 rounded-xl font-medium transition-colors"
                  style={{ 
                    color: 'rgba(191, 155, 90, 0.7)',
                    backgroundColor: 'transparent',
                    border: 'none'
                  }}
                  data-testid="onboarding-maybe-later"
                >
                  {loading ? "Setting up..." : "Maybe later"}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
